import { useEffect, useRef, useState } from "react";
import { ShieldCheck, Zap, Database, Truck } from "lucide-react";
import Footer from "./Footer";

import video     from "../images/aivedio.mp4";
import container from "../images/Container.png";
import client1   from "../images/client-1.png";
import client2   from "../images/client-2.png";
import client3   from "../images/client-3.png";
import client4   from "../images/client-4.png";
import client5   from "../images/client-5.png";
import logo      from "../images/amiLogo.jpg";
import heroImg1  from "../images/client-1.png";
import heroImg2  from "../images/client-2.png";
import heroImg3  from "../images/client-3.png";
import heroImg4  from "../images/client-4.png";

const cards = [
  { icon: <ShieldCheck size={22}/>, title:"Surveillance",  desc:"End-to-end tactical processing for perimeter defense and strategic forecasting without external dependencies.", tag:"LEVEL 05 AUTONOMY" },
  { icon: <Database   size={22}/>, title:"Data Centers",  desc:"Localized LLM architectures that ensure sensitive citizen data remains within national borders.",                tag:"ZERO-TRUST PROTOCOL" },
  { icon: <Zap        size={22}/>, title:"Metro",         desc:"Seamless crowd analytics and safety monitoring across metro and transit networks in real time.",                tag:"REAL-TIME OPTIMIZATION" },
  { icon: <Truck      size={22}/>, title:"4G / 5G",       desc:"Real-time AI logic atop 5G networks for predictive load balancing and low-latency processing.",                tag:"PREDICTIVE RECOVERY" },
];

const partners = [
  { img: client1, name: "Sovereign Systems", desc: "National Data" },
  { img: client2, name: "GridCore AI",       desc: "Energy Grid" },
  { img: client3, name: "DefenseTech",       desc: "Defense Intel" },
  { img: client4, name: "UrbanFlow",         desc: "Urban Mobility" },
  { img: client5, name: "TeleNet 5G",        desc: "Telecoms" },
];

const allLogos = [client1, client2, client3, client4, client5];

export default function Platform() {
  const [hov, setHov]   = useState(null);
  const ecoRef          = useRef(null);
  const waveCanvasRef   = useRef(null);
  const titleRef        = useRef(null);

  /* ── Scroll reveal ── */
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add("vis"); obs.unobserve(e.target); }
      }),
      { threshold: 0.08 }
    );
    document.querySelectorAll(".anim").forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  /* ── Sky-blue wave canvas ── */
  useEffect(() => {
    const canvas = waveCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let W, H, raf, t = 0;

    const resize = () => {
      const parent = canvas.parentElement;
      W = canvas.width  = parent.offsetWidth;
      H = canvas.height = parent.offsetHeight;
    };

    const drawWaves = () => {
      raf = requestAnimationFrame(drawWaves);
      t += 0.008;
      ctx.clearRect(0, 0, W, H);

      const waves = [
        { amp: H * 0.06, freq: 0.008, phase: t,       yBase: H * 0.55, color: "rgba(56,189,248,0.18)" },
        { amp: H * 0.08, freq: 0.006, phase: t + 1.0, yBase: H * 0.62, color: "rgba(14,165,233,0.14)" },
        { amp: H * 0.05, freq: 0.010, phase: t + 2.0, yBase: H * 0.70, color: "rgba(56,189,248,0.22)" },
        { amp: H * 0.09, freq: 0.005, phase: t + 0.5, yBase: H * 0.78, color: "rgba(7,146,210,0.16)"  },
      ];

      waves.forEach(w => {
        ctx.beginPath();
        ctx.moveTo(0, H);
        for (let x = 0; x <= W; x += 3) {
          const y = w.yBase + Math.sin(x * w.freq + w.phase) * w.amp;
          ctx.lineTo(x, y);
        }
        ctx.lineTo(W, H);
        ctx.closePath();
        ctx.fillStyle = w.color;
        ctx.fill();
      });

      const orbs = [
        { cx: W * 0.15, cy: H * 0.7 + Math.sin(t * 0.7) * 18, r: 60 },
        { cx: W * 0.5,  cy: H * 0.5 + Math.sin(t * 0.5 + 1) * 22, r: 80 },
        { cx: W * 0.82, cy: H * 0.65 + Math.sin(t * 0.6 + 2) * 16, r: 55 },
      ];
      orbs.forEach(o => {
        const g = ctx.createRadialGradient(o.cx, o.cy, 0, o.cx, o.cy, o.r);
        g.addColorStop(0, "rgba(56,189,248,0.14)");
        g.addColorStop(1, "rgba(56,189,248,0)");
        ctx.beginPath();
        ctx.arc(o.cx, o.cy, o.r, 0, Math.PI * 2);
        ctx.fillStyle = g;
        ctx.fill();
      });
    };

    window.addEventListener("resize", resize);
    resize();
    drawWaves();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  /* ── Neural network canvas ── */
  useEffect(() => {
    const section = ecoRef.current;
    if (!section) return;
    const canvas = document.getElementById("eco-canvas");
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const NODE_COUNT = 62, MAX_DIST = 130;
    let W, H, nodes = [], raf;
    let mouse = { x: -9999, y: -9999 };

    const resize = () => {
      const r = section.getBoundingClientRect();
      W = canvas.width  = r.width  || 1100;
      H = canvas.height = r.height || 520;
    };
    const mkNode = () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - .5) * .42, vy: (Math.random() - .5) * .42,
      r: Math.random() * 2 + 1.5, pulse: Math.random() * Math.PI * 2,
    });
    const init = () => {
      resize();
      nodes = Array.from({ length: NODE_COUNT }, mkNode);
      if (raf) cancelAnimationFrame(raf);
      draw();
    };
    const draw = () => {
      raf = requestAnimationFrame(draw);
      ctx.clearRect(0, 0, W, H);
      nodes.forEach(n => {
        n.x += n.vx; n.y += n.vy; n.pulse += .018;
        if (n.x < 0 || n.x > W) n.vx *= -1;
        if (n.y < 0 || n.y > H) n.vy *= -1;
      });
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j], d = Math.hypot(a.x-b.x, a.y-b.y);
          if (d < MAX_DIST) {
            ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = `rgba(56,189,248,${(1-d/MAX_DIST)*.3})`; ctx.lineWidth = .7; ctx.stroke();
          }
        }
      }
      nodes.forEach(n => {
        const d = Math.hypot(n.x-mouse.x, n.y-mouse.y);
        if (d < 170) {
          ctx.beginPath(); ctx.moveTo(n.x, n.y); ctx.lineTo(mouse.x, mouse.y);
          ctx.strokeStyle = `rgba(125,211,252,${(1-d/170)*.55})`; ctx.lineWidth = 1; ctx.stroke();
        }
      });
      nodes.forEach(n => {
        const p = .6 + Math.sin(n.pulse) * .4;
        ctx.beginPath(); ctx.arc(n.x,n.y,n.r*2.8,0,Math.PI*2); ctx.fillStyle=`rgba(56,189,248,${.055*p})`; ctx.fill();
        ctx.beginPath(); ctx.arc(n.x,n.y,n.r,0,Math.PI*2); ctx.fillStyle=`rgba(186,230,253,${.5+.45*p})`; ctx.fill();
        ctx.beginPath(); ctx.arc(n.x,n.y,n.r*.42,0,Math.PI*2); ctx.fillStyle=`rgba(255,255,255,${.7*p})`; ctx.fill();
      });
    };
    const onMove  = e => { const r=canvas.getBoundingClientRect(); mouse.x=e.clientX-r.left; mouse.y=e.clientY-r.top; };
    const onLeave = () => { mouse.x=-9999; mouse.y=-9999; };
    section.addEventListener("mousemove", onMove);
    section.addEventListener("mouseleave", onLeave);
    window.addEventListener("resize", init);
    init();
    return () => {
      if (raf) cancelAnimationFrame(raf);
      section.removeEventListener("mousemove", onMove);
      section.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("resize", init);
    };
  }, []);

  return (
    <>
      <style>{`
        /* ── POPPINS ONLY — replaces DM Sans + Syne ── */
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');

        *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }

        /* Every element uses Poppins */
        .pr  { font-family:'Poppins',sans-serif; }

        /* .sy was Syne bold display — now Poppins 800 */
        .sy  { font-family:'Poppins',sans-serif; font-weight:800; }

        /* ── scroll reveal ── */
        .anim { opacity:0; transform:translateY(38px); transition:opacity .8s ease,transform .8s ease; }
        .anim.vis { opacity:1; transform:translateY(0); }
        .anim-d1{transition-delay:.08s} .anim-d2{transition-delay:.18s}
        .anim-d3{transition-delay:.28s} .anim-d4{transition-delay:.38s}
        .anim-d5{transition-delay:.48s}

        /* ── blue accent line ── */
        .bline { display:block; height:3px; width:40px; background:linear-gradient(90deg,#38bdf8,#0284c7); border-radius:3px; margin-bottom:14px; }

        /* ── pulse dot ── */
        .pdot { width:8px; height:8px; border-radius:50%; background:#0ea5e9; flex-shrink:0; animation:pdotA 2s ease-in-out infinite; }
        @keyframes pdotA { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.3;transform:scale(1.7)} }

        /* ── hero image strip (BW) ── */
        .img-strip { display:grid; grid-template-columns:repeat(4,1fr); gap:6px; }
        .img-cell { position:relative; overflow:hidden; border-radius:8px; border:0.5px solid rgba(255,255,255,.12); }
        .img-cell img { width:100%; height:140px; object-fit:cover; display:block; filter:grayscale(1) brightness(.65); transition:filter .35s,transform .35s; }
        .img-cell:hover img { filter:grayscale(.2) brightness(.85); transform:scale(1.04); }
        .img-cell-ov { position:absolute; inset:0; background:linear-gradient(to top,rgba(0,0,0,.65),transparent); }
        .img-cell-lbl { position:absolute; bottom:7px; left:9px; font-size:10px; font-family:'Poppins',sans-serif; font-weight:600; color:rgba(255,255,255,.85); letter-spacing:2px; text-transform:uppercase; }

        /* ── hero wave cut ── */
        .hero-wave { position:absolute; bottom:-2px; left:0; width:100%; pointer-events:none; z-index:6; }

        /* ── scan line anim ── */
        @keyframes scanLine { 0%{top:-4%} 100%{top:108%} }
        .scan-line { position:absolute; left:0; right:0; height:2px; background:linear-gradient(90deg,transparent,rgba(56,189,248,.3),transparent); animation:scanLine 6s linear infinite; pointer-events:none; z-index:4; }

        /* ── floating particles ── */
        .ptcl { position:absolute; border-radius:50%; animation:floatUp 7s ease-in-out infinite; pointer-events:none; }
        @keyframes floatUp { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-20px)} }

        /* ── cap card ── */
        .cap-card { transition:transform .3s,box-shadow .3s,border-color .3s !important; }
        .cap-card:hover { transform:translateY(-7px) !important; }
        .cap-card::after { content:''; position:absolute; bottom:0; left:0; height:3px; width:0; background:linear-gradient(90deg,#38bdf8,#0ea5e9); transition:width .4s ease; border-radius:0 0 12px 12px; }
        .cap-card:hover::after { width:100%; }

        /* ── glass partner card ── */
        .pcard-glass { border-radius:14px; padding:20px 14px; display:flex; flex-direction:column; align-items:center; gap:11px; cursor:pointer; position:relative; overflow:hidden; background:rgba(255,255,255,.06); border:1px solid rgba(56,189,248,.2); transition:transform .35s,border-color .35s,background .35s; backdrop-filter:blur(8px); }
        .pcard-glass:hover { transform:translateY(-8px); border-color:rgba(56,189,248,.6); background:rgba(56,189,248,.1); }
        .pcard-glass::after { content:''; position:absolute; top:0; left:0; height:2px; width:0; background:linear-gradient(90deg,#38bdf8,#0284c7); transition:width .4s ease; }
        .pcard-glass:hover::after { width:100%; }
        .pcard-glow { position:absolute; inset:0; background:radial-gradient(circle at 50% 0%,rgba(56,189,248,.12),transparent 65%); opacity:0; transition:opacity .35s; pointer-events:none; }
        .pcard-glass:hover .pcard-glow { opacity:1; }
        .pglass-logo { width:100%; height:48px; border-radius:8px; display:flex; align-items:center; justify-content:center; background:rgba(255,255,255,.07); border:1px solid rgba(56,189,248,.15); transition:background .3s; }
        .pcard-glass:hover .pglass-logo { background:rgba(56,189,248,.15); }
        .pglass-logo img { max-height:30px; max-width:100px; object-fit:contain; filter:brightness(0) invert(1); opacity:.6; transition:opacity .3s,transform .3s; }
        .pcard-glass:hover .pglass-logo img { opacity:1; transform:scale(1.06); }
        .pglass-name { font-size:11.5px; font-weight:600; color:#e0f2fe; text-align:center; font-family:'Poppins',sans-serif; }
        .pglass-desc { font-size:9px; font-weight:500; color:#38bdf8; text-transform:uppercase; letter-spacing:1.5px; text-align:center; opacity:.7; font-family:'Poppins',sans-serif; }
        .pglass-dot  { width:5px; height:5px; border-radius:50%; background:rgba(56,189,248,.3); transition:background .3s; }
        .pcard-glass:hover .pglass-dot { background:#38bdf8; }

        /* ── ticker ── */
        @keyframes tickMove { from{transform:translateX(0)} to{transform:translateX(-50%)} }
        .tick-track { display:flex; gap:40px; width:max-content; animation:tickMove 22s linear infinite; }
        .tick-track:hover { animation-play-state:paused; }

        /* ── wave canvas ── */
        .wave-canvas { position:absolute; inset:0; width:100%; height:100%; pointer-events:none; z-index:0; }

        @media(max-width:640px){
          .img-strip{ grid-template-columns:repeat(2,1fr); }
          .img-cell img{ height:90px; }
        }
      `}</style>

      <div className="pr" style={{ paddingTop: 64 }}>

        {/* ══════════════════════════════════════════════
            HERO — BW grayscale video
        ══════════════════════════════════════════════ */}
        <section className="h-screen sticky top-0 relative overflow-hidden"
          style={{ background: "#0a0a14" }}>

          <video src={video} autoPlay loop muted playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: "grayscale(1) brightness(.5) contrast(1.1)", opacity: 1 }}
          />

          <div className="absolute inset-0" style={{
            background: "linear-gradient(180deg,rgba(0,0,0,.55) 0%,rgba(0,0,0,.3) 35%,rgba(0,0,0,.05) 60%,transparent 100%)",
            zIndex: 2,
          }}/>

          <div className="scan-line" style={{ zIndex: 3 }}/>

          {[
            { w:10, t:"9%",  l:"7%",  d:"0s"   },
            { w:6,  t:"20%", r:"9%",  d:".9s"  },
            { w:14, t:"7%",  r:"20%", d:"1.8s" },
            { w:7,  t:"17%", l:"30%", d:"2.5s" },
          ].map((p, i) => (
            <div key={i} className="ptcl" style={{
              width: p.w, height: p.w, top: p.t, left: p.l, right: p.r,
              animationDelay: p.d, background: "rgba(255,255,255,.15)", zIndex: 3,
            }}/>
          ))}

         

          <svg className="hero-wave" viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ height: 80 }}>
            <path d="M0,50 C280,80 700,10 1080,50 C1260,68 1380,30 1440,50 L1440,80 L0,80 Z" fill="#f0f8ff"/>
          </svg>
        </section>


        {/* ══════════════════════════════════════════════
            CAPABILITIES — animated sky-blue wave bg
        ══════════════════════════════════════════════ */}
        <section className="relative z-10"
          style={{ background: "linear-gradient(180deg,#f0f8ff 0%,#dbeeff 50%,#bae6fd 100%)" }}>

          {/* Title that fades in on scroll */}
          <div className="text-center anim py-16 px-6" ref={titleRef}>
            <h2
              className="sy"
              style={{
                fontFamily: "'Poppins',sans-serif",
                fontSize: "clamp(28px,4.5vw,52px)",
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: "-0.5px",
                color: "#0c1a2e",
                marginBottom: 20,
              }}
            >
              Intelligence. Scaled.<br/>
              <span style={{ color: "#0284c7" }}>Platformizing the</span><br/>
              Phygital World.
            </h2>
            {/* <button
              style={{
                fontFamily: "'Poppins',sans-serif",
                fontSize: 12,
                fontWeight: 700,
                color: "#fff",
                background: "linear-gradient(90deg,#0284c7,#38bdf8)",
                border: "none",
                borderRadius: 24,
                padding: "11px 32px",
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                cursor: "pointer",
                boxShadow: "0 4px 24px rgba(56,189,248,.35)",
              }}
            >
              Learn more now
            </button> */}
          </div>

          <canvas ref={waveCanvasRef} className="wave-canvas"/>

          <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 pb-20">

            <div className="mb-12 anim">
              <span className="bline"/>
              <p style={{ fontFamily:"'Poppins',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"3px", textTransform:"uppercase", color:"#0284c7", marginBottom:10 }}>
                Intelligence Layer
              </p>
              <h2 style={{ fontFamily:"'Poppins',sans-serif", fontSize:"clamp(24px,3.5vw,36px)", fontWeight:800, color:"#0c1a2e", letterSpacing:"-0.5px", marginBottom:10 }}>
                Capabilities
              </h2>
              <p style={{ fontFamily:"'Poppins',sans-serif", fontSize:13.5, fontWeight:400, color:"#3a6a8a", maxWidth:520, lineHeight:1.7 }}>
                Specialized artificial intelligence frameworks designed to handle the specific volatility of national-scale assets and sovereign interests.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {cards.map((card, i) => (
                <div key={i}
                  className={`cap-card anim anim-d${i+1} relative overflow-hidden rounded-2xl p-6 cursor-pointer`}
                  style={{
                    background: "rgba(255,255,255,.88)",
                    border: "1px solid rgba(56,189,248,.25)",
                    boxShadow: "0 4px 20px rgba(14,165,233,.1)",
                    backdropFilter: "blur(8px)",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor="#38bdf8"; e.currentTarget.style.boxShadow="0 16px 48px rgba(56,189,248,.25)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor="rgba(56,189,248,.25)"; e.currentTarget.style.boxShadow="0 4px 20px rgba(14,165,233,.1)"; }}
                >
                  <div style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width:28, height:28, borderRadius:"50%", background:"#e0f2fe", color:"#0284c7", fontFamily:"'Poppins',sans-serif", fontSize:10, fontWeight:700, marginBottom:18 }}>
                    0{i+1}
                  </div>
                  <div style={{ width:44, height:44, borderRadius:12, background:"linear-gradient(135deg,#e0f2fe,#bae6fd)", color:"#0284c7", display:"flex", alignItems:"center", justifyContent:"center", marginBottom:16 }}>
                    {card.icon}
                  </div>
                  <h3 style={{ fontFamily:"'Poppins',sans-serif", fontSize:14, fontWeight:700, color:"#0c1a2e", marginBottom:8 }}>{card.title}</h3>
                  <p style={{ fontFamily:"'Poppins',sans-serif", fontSize:12, fontWeight:400, color:"#3a6a8a", lineHeight:1.65, marginBottom:16 }}>{card.desc}</p>
                  <span style={{ display:"inline-block", fontFamily:"'Poppins',sans-serif", fontSize:9, fontWeight:700, letterSpacing:"1.5px", textTransform:"uppercase", background:"#e0f2fe", color:"#0284c7", padding:"3px 10px", borderRadius:10 }}>
                    {card.tag}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* ══════════════════════════════
            METRICS
        ══════════════════════════════ */}
        <section style={{
          background: "#dbeeff", position: "relative", zIndex: 20,
          isolation: "isolate", overflow: "hidden",
          borderTop: "2px solid #bae6fd", borderBottom: "2px solid #bae6fd",
        }}>
          <div style={{ position:"absolute", inset:0, pointerEvents:"none", zIndex:0, backgroundImage:"radial-gradient(circle,rgba(14,165,233,.1) 1px,transparent 1px)", backgroundSize:"28px 28px" }}/>
          <div style={{ position:"absolute", left:"-8%", top:"-20%", width:560, height:560, borderRadius:"50%", pointerEvents:"none", zIndex:0, background:"radial-gradient(circle,rgba(56,189,248,.15) 0%,transparent 70%)" }}/>

          <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-16 py-14 md:py-20 flex flex-col lg:flex-row items-center gap-12 lg:gap-16">

            <div className="flex-1 min-w-0 anim">
              <span className="bline"/>
              <p style={{ fontFamily:"'Poppins',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"3px", textTransform:"uppercase", color:"#0284c7", marginBottom:12 }}>
                Performance Metrics
              </p>
              <h2 style={{ fontFamily:"'Poppins',sans-serif", fontSize:"clamp(40px,6vw,64px)", fontWeight:800, color:"#0c1a2e", lineHeight:1, letterSpacing:"-1.5px", marginBottom:10 }}>
                384 Billion
              </h2>
              <p style={{ fontFamily:"'Poppins',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"3px", textTransform:"uppercase", color:"#0ea5e9", marginBottom:18 }}>
                Autonomous Decisions Per Cycle
              </p>
              <p style={{ fontFamily:"'Poppins',sans-serif", fontSize:13.5, fontWeight:400, color:"#2a5a7a", maxWidth:420, lineHeight:1.7, marginBottom:28 }}>
                AMI Paradigm's core distillation engine processes petabytes of telemetry into actionable governance. This isn't just data — it's the heartbeat of a functioning state.
              </p>
              <div className="flex flex-col sm:flex-row gap-8 pt-5" style={{ borderTop: "1px solid rgba(14,165,233,.22)" }}>
                {[
                  { label: "Processing Latency",    value: "0.02ms Core Response",     delay: "0s"  },
                  { label: "Model Parameter Count", value: "1.8T Specialized Weights", delay: ".8s" },
                ].map((s, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="pdot mt-1 flex-shrink-0" style={{ animationDelay: s.delay }}/>
                    <div>
                      <p style={{ fontFamily:"'Poppins',sans-serif", fontSize:9.5, fontWeight:700, letterSpacing:"2px", textTransform:"uppercase", color:"#5a9ab5", marginBottom:4 }}>{s.label}</p>
                      <p style={{ fontFamily:"'Poppins',sans-serif", fontSize:13.5, fontWeight:700, color:"#0c1a2e" }}>{s.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex-none flex justify-center w-full lg:w-auto anim anim-d2">
              <div className="w-full rounded-2xl p-4 relative"
                style={{ maxWidth:380, background:"rgba(255,255,255,.88)", border:"1px solid #7dd3fc", boxShadow:"0 24px 64px rgba(56,189,248,.22),0 4px 16px rgba(14,165,233,.1)" }}>
                <div className="absolute top-0 left-0 w-7 h-7 border-t-2 border-l-2 border-sky-400 rounded-tl-2xl"/>
                <div className="absolute bottom-0 right-0 w-7 h-7 border-b-2 border-r-2 border-sky-400 rounded-br-2xl"/>
                <img src={container} alt="AI Infrastructure" className="w-full rounded-xl object-cover" style={{ height:200, opacity:.95, display:"block" }}/>
                <div className="mt-3 rounded-xl px-4 py-2.5 flex items-center gap-3"
                  style={{ background:"#fff", border:"1px solid #bae6fd", boxShadow:"0 2px 12px rgba(56,189,248,.12)" }}>
                  <div className="pdot flex-shrink-0"/>
                  <div>
                    <p style={{ fontFamily:"'Poppins',sans-serif", fontSize:9, fontWeight:700, letterSpacing:"2px", textTransform:"uppercase", color:"#0284c7" }}>System Status</p>
                    <p style={{ fontFamily:"'Poppins',sans-serif", fontSize:12, fontWeight:600, color:"#0c1a2e" }}>All nodes operational · 99.9% uptime</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  {[{ val:"248", label:"Active Nodes" },{ val:"12ms", label:"Avg Latency" },{ val:"99.9%", label:"Uptime" }].map((chip, i) => (
                    <div key={i} className="flex-1 rounded-xl py-3 text-center"
                      style={{ background:"linear-gradient(135deg,#e0f2fe,#f0f9ff)", border:"1px solid #bae6fd" }}>
                      <p style={{ fontFamily:"'Poppins',sans-serif", fontSize:18, fontWeight:800, color:"#0284c7", lineHeight:1, marginBottom:3 }}>{chip.val}</p>
                      <p style={{ fontFamily:"'Poppins',sans-serif", fontSize:8.5, fontWeight:600, letterSpacing:"1.5px", textTransform:"uppercase", color:"#5a9ab5" }}>{chip.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* ══════════════════════════════
            ECOSYSTEM — Neural Canvas
        ══════════════════════════════ */}
        <div ref={ecoRef} style={{ position:"relative", overflow:"hidden", background:"#030e1f", isolation:"isolate", zIndex:10, padding:"72px 0 60px" }}>
          <canvas id="eco-canvas" style={{ position:"absolute", inset:0, width:"100%", height:"100%", zIndex:0, opacity:.9 }}/>
          <div style={{ position:"absolute", top:0, left:0, right:0, height:90, background:"linear-gradient(to bottom,rgba(14,116,180,.12),transparent)", pointerEvents:"none", zIndex:1 }}/>
          <div style={{ position:"absolute", bottom:0, left:0, right:0, height:80, background:"linear-gradient(to top,rgba(3,14,31,1),transparent)", pointerEvents:"none", zIndex:1 }}/>

          <div className="relative max-w-6xl mx-auto px-6" style={{ zIndex: 2 }}>
            <div className="text-center mb-14 anim">
              <span className="bline mx-auto"/>
              <p style={{ fontFamily:"'Poppins',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"3px", textTransform:"uppercase", color:"#38bdf8", marginBottom:8 }}>Ecosystem</p>
              <h2 style={{ fontFamily:"'Poppins',sans-serif", fontSize:"clamp(24px,3.5vw,38px)", fontWeight:800, color:"#fff", letterSpacing:"-0.5px", marginBottom:10 }}>
                Information Partners
              </h2>
              <p style={{ fontFamily:"'Poppins',sans-serif", fontSize:13.5, fontWeight:400, color:"#5a9ab5", maxWidth:420, margin:"0 auto", lineHeight:1.7 }}>
                Trusted by the organisations shaping the infrastructure of sovereign nations.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 mb-10">
              {partners.map((p, i) => (
                <div key={i} className={`pcard-glass anim anim-d${i+1}`}
                  onMouseEnter={() => setHov(i)} onMouseLeave={() => setHov(null)}>
                  <div className="pcard-glow"/>
                  <div className=""><img src={p.img} alt={p.name}/></div>
                  <p className="pglass-name">{p.name}</p>
                  <p className="pglass-desc">{p.desc}</p>
                  <div className="pglass-dot"/>
                </div>
              ))}
            </div>
{/* 
            <div className="rounded-2xl py-4 px-6 overflow-hidden"
              style={{ background:"rgba(255,255,255,.04)", border:"1px solid rgba(56,189,248,.12)", backdropFilter:"blur(6px)" }}>
              <p className="text-center mb-3"
                style={{ fontFamily:"'Poppins',sans-serif", fontSize:9, fontWeight:700, letterSpacing:"2.5px", textTransform:"uppercase", color:"rgba(56,189,248,.4)" }}>
                Partner Network — Live
              </p>
              <div style={{ overflow: "hidden" }}>
                <div className="tick-track">
                  {[...allLogos,...allLogos,...allLogos].map((img, i) => (
                    <img key={i} src={img} alt="partner"
                      style={{ height:28, objectFit:"contain", filter:"brightness(0) invert(1)", opacity:.45, flexShrink:0, transition:"opacity .3s" }}
                      onMouseEnter={e => (e.currentTarget.style.opacity = "1")}
                      onMouseLeave={e => (e.currentTarget.style.opacity = ".45")}
                    />
                  ))}
                </div>
              </div>
            </div> */}
          </div>
        </div>

      </div>

      <Footer/>
    </>
  );
}